import { Injectable, inject, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { PaymentLinksServiceV2 } from 'api-client';
import { LogService } from 'shared';

@Injectable({
  providedIn: 'root',
})
export class DeepLinkService {
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly logger = inject(LogService);
  private readonly paymentLinksService = inject(PaymentLinksServiceV2);

  initialize(): void {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(() => {
        this.handleUrl(event.url);
      });
    });
  }

  private handleUrl(url: string): void {
    this.logger.info('Deep link received:', 'DeepLinkService.handleUrl', url);

    try {
      const parsed = new URL(url);
      const host = parsed.hostname;
      const path = parsed.pathname;

      // Payment links: payment.dreampay.com.br/pay/{uuid}
      if (host === 'payment.dreampay.com.br') {
        const payMatch = path.match(/^\/pay\/([a-f0-9-]+)$/i);
        if (payMatch) {
          void this.router.navigate(['/payments/pay', payMatch[1]]);
          return;
        }
      }

      // Shortened payment links: links.dreampay.com.br/{code}
      if (host === 'links.dreampay.com.br') {
        const code = path.replace(/^\//, '');
        if (code) {
          this.resolveShortenedLink(code);
          return;
        }
      }

      // Auth links: app.dreampay.com.br/auth/...
      if (host === 'app.dreampay.com.br') {
        // Email verification: /auth/email-verification?token=...&email=...
        if (path === '/auth/email-verification') {
          const token = parsed.searchParams.get('token');
          const email = parsed.searchParams.get('email');
          if (token && email) {
            void this.router.navigate(['/auth/email-verification'], {
              queryParams: { token, email },
            });
            return;
          }
        }

        // Reset password: /auth/reset-password?token=...&email=...
        if (path === '/auth/reset-password') {
          const token = parsed.searchParams.get('token');
          const email = parsed.searchParams.get('email');
          if (token && email) {
            void this.router.navigate(['/auth/reset-password'], {
              queryParams: { token, email },
            });
            return;
          }
        }
      }

      this.logger.warn('Unhandled deep link:', 'DeepLinkService.handleUrl', url);
    } catch (error) {
      this.logger.error('Failed to parse deep link:', 'DeepLinkService.handleUrl', error);
    }
  }

  private resolveShortenedLink(shortenedCode: string): void {
    this.paymentLinksService.apiV2PaymentLinksShortenedShortenedCodeGet(shortenedCode).subscribe({
      next: (paymentLink) => {
        if (paymentLink?.id) {
          void this.router.navigate(['/payments/pay', paymentLink.id]);
        } else {
          this.logger.warn(
            'Shortened link resolved but no ID:',
            'DeepLinkService.resolveShortenedLink',
            shortenedCode,
          );
        }
      },
      error: (err) => {
        this.logger.error(
          'Failed to resolve shortened link:',
          'DeepLinkService.resolveShortenedLink',
          err,
        );
      },
    });
  }
}
